import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fmtCurrency, niceName } from "@/lib/format";
import { CardTag } from "@/components/CardTag";
import { ContextLine, seloOf } from "@/components/ContextLine";

const deedIso = (big) => {
  const n = parseInt(big, 10);
  if (Number.isNaN(n)) return null;
  const d = new Date(); d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const PHASE_NAMES = ["Angariação", "CMI assinado", "Proposta aceite", "CPCV", "Escritura"];
const BAR_HEX = "#1F5F8B", TODO_HEX = "#E5E7EB";
export const STATE = {
  amber: { hex: "#B45309", tag: "bg-amber-50 text-[#B45309]", corner: "bg-amber-50 text-[#B45309]" },
  red: { hex: "#C2261D", tag: "bg-red-50 text-[#C2261D]", corner: "bg-red-50 text-[#C2261D]" },
  green: { hex: "#15803D", tag: "bg-green-50 text-[#15803D]", corner: "bg-green-50 text-[#15803D]" },
  grey: { hex: "#64748B", tag: "bg-slate-100 text-[#64748B]", corner: "bg-slate-100 text-slate-500" },
};

// Concluídas e atual a azul (#1F5F8B), futuras a cinzento claro; o estado fica só no texto.
export function PhaseSegments({ phase, testid, names = false, namesClass = "", vivid = false, labels = PHASE_NAMES }) {
  const [tip, setTip] = useState(null);
  const cur = BAR_HEX;
  return (
    <div data-testid={testid}>
      <div className={`grid grid-cols-5 ${vivid ? "gap-1" : "gap-1.5"}`}>
        {labels.map((n, i) => (
          <span key={n} title={n} aria-label={n} role="button" tabIndex={0} className="py-1.5 -my-1.5 cursor-help" data-testid={`${testid}-seg-${i}`}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setTip(tip === i ? null : i); }}>
            <span className={`block ${vivid ? "h-2" : "h-1.5"} rounded-full`} data-state={i < phase ? "done" : i === phase ? "current" : "todo"}
              style={{ backgroundColor: i <= phase ? BAR_HEX : TODO_HEX }} />
          </span>
        ))}
      </div>
      {names && <div className={`grid grid-cols-5 gap-1.5 mt-1 ${namesClass}`} data-testid={`${testid}-names`}>
        {labels.map((n, i) => <span key={n} className={`text-[10px] leading-tight break-words hyphens-auto ${i === phase ? "font-bold" : "text-slate-500"}`} style={i === phase ? { color: cur } : undefined} lang="pt" data-testid={`${testid}-name-${i}`}>{n}</span>)}
      </div>}
      {tip !== null && <div className="text-xs text-slate-500 mt-1" data-testid={`${testid}-tip`}>Fase {tip + 1}: {labels[tip]}</div>}
    </div>
  );
}

// big: versão do computador em O meu dia (lg: letra ≥ 14 px, morada inteira, barra sem nomes).
export function ProcessCard({ id, card: c, extra, testid, big = false }) {
  const navigate = useNavigate();
  const s = STATE[c.tone] || STATE.amber;
  const who = niceName(c.consultant) || "Sem consultor";
  const initials = who.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
  const go = () => navigate(`/processo/${id}`);
  const bar = c.tone === "red" ? "border-l-4 border-l-red-600" : c.tone === "amber" ? "border-l-4 border-l-amber-500" : "";
  const selo = seloOf({ deedDate: c.corner?.small === "à escritura" ? deedIso(c.corner.big) : null, block: (c.blocks || []).length > 0, card: c });
  return (
    <div role="link" tabIndex={0} onClick={go} onKeyDown={(e) => e.key === "Enter" && go()} data-testid={testid}
      className={`group block cursor-pointer bg-white rounded-[18px] p-4 md:p-[18px] border border-slate-100 ${bar} shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0_18px_44px_rgb(0,0,0,0.08)] transition-[transform,box-shadow] duration-300`}>
      <div className={`text-xs text-slate-400 tabular-nums ${big ? "lg:text-sm lg:text-slate-500" : ""}`} data-testid={`${testid}-num`}>{c.num}{c.prefix ? ` · ${c.prefix}` : ""}</div>
      <h3 className="font-semibold text-[17px] leading-snug navy-text mt-0.5" data-testid={`${testid}-title`}>
        <ContextLine where={c.address || "Morada por preencher"} selo={selo} testid={`${testid}-corner`} wrap={big} big={big} />
      </h3>
      <p className={`text-sm text-slate-600 mt-2 truncate ${big ? "lg:whitespace-normal" : ""}`} data-testid={`${testid}-parties`}>{c.parties}{c.price && !big ? ` · ${fmtCurrency(c.price)}` : ""}</p>
      {big && c.price ? <div className="text-[26px] leading-tight font-bold navy-text tabular-nums mt-1" data-testid={`${testid}-price`}>{fmtCurrency(c.price)}</div> : null}
      <div className="mt-3"><PhaseSegments phase={c.phase} tone={c.tone} testid={`${testid}-phases`} names namesClass={big ? "lg:hidden" : ""} vivid={big} /></div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
        <span className="text-sm text-slate-500">Fase: <b className="font-semibold navy-text">{c.phase_label}</b></span>
        <CardTag pid={id} card={c} big={big} className={`text-[13px] ${big ? "lg:text-sm" : ""} font-semibold px-2.5 py-0.5 rounded-full ${s.tag}`} testid={`${testid}-tag`} />
      </div>
      {c.next && <p className="text-sm text-slate-700 mt-3 pt-3 border-t border-slate-100" data-testid={`${testid}-next`}><b className="font-semibold navy-text">Próximo:</b> {c.next}</p>}
      <div className={`flex items-center justify-between gap-2 mt-3 text-sm text-slate-500 ${big ? "lg:text-slate-600" : ""}`}>
        <span className="flex items-center gap-2"><span className={`grid place-items-center w-7 h-7 rounded-full bg-slate-100 text-slate-600 font-semibold text-[11px] ${big ? "lg:w-8 lg:h-8 lg:text-sm" : ""}`}>{initials}</span>{who}</span>
        {extra}
      </div>
    </div>
  );
}
